import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailCustomerCardIssues,
  type CustomerCardIssue,
} from '@/api/retailCustomerCardIssue';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'retail.customer_card.manage';

export default function CustomerCardIssuesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-customer-card-issues', orgId],
    queryFn: () =>
      retailCustomerCardIssues.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<CustomerCardIssue>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'customer_card_id',
        header: 'Картка',
        sortValue: (r) => r.customer_card_id ?? '',
        cell: (r) => r.customer_card_id,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 140,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати картками клієнтів (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Видача карток клієнтам
          <HintBadge
            title="Що це?"
            text={
              'Документ-журнал видачі карток лояльності клієнтам. ' +
              'Фіксує: КОЛИ, КОМУ і ХТО з касирів видав картку.\n\n' +
              'Навіщо окремий документ:\n' +
              '• Для статистики «скільки карток роздали за місяць».\n' +
              '• Для відстеження активності касира («Iванова видала 50 карток у вересні»).\n' +
              '• Прив\'язка до POS-станції — де саме клієнт отримав картку.\n\n' +
              'Створюється касиром на POS або вручну адміном. Сама картка створюється ' +
              'у «Картки клієнтів», а тут — лише запис про факт видачі.'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/customer-card-issue/new')}
        >
          + Нова видача
        </button>
      </div>
      <DataTable<CustomerCardIssue>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/retail/customer-card-issue/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
